import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface SplashScreenProps {
  onComplete: () => void;
}

const SplashScreen: React.FC<SplashScreenProps> = ({ onComplete }) => {
  const [complete, setComplete] = useState(false);
  // Utilise un ref pour éviter que l'effet se ré-déclenche si onComplete change
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  useEffect(() => {
    // Bloquer uniquement le scroll — PAS position:fixed qui cause une page blanche sur mobile
    document.body.style.overflow = 'hidden';

    const timer = setTimeout(() => {
      setComplete(true);
    }, 4500);

    const removeTimer = setTimeout(() => {
      // Restaurer le scroll
      document.body.style.overflow = '';
      onCompleteRef.current();
    }, 5500);

    return () => {
      // Nettoyage si le composant se démonte avant la fin
      document.body.style.overflow = '';
      clearTimeout(timer);
      clearTimeout(removeTimer);
    };
  }, []); // Dépendances vides : ne se déclenche qu'une seule fois au montage

  return (
    <AnimatePresence>
      {!complete && (
        <motion.div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#ECF0F1] overflow-hidden w-screen h-[100dvh] touch-none"
          exit={{ opacity: 0, y: -20, filter: "blur(10px)" }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
        >
          {/* Logo Illustration Container */}
          <div className="relative w-56 h-56 md:w-72 md:h-72 flex items-center justify-center z-10">

            {/* Globe Shape - Morphs from blur to solid */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ filter: "blur(60px)", scale: 0.3, opacity: 0, rotate: -90, x: -80, y: 80 }}
              animate={{ filter: "blur(0px)", scale: 1, opacity: 1, rotate: 0, x: 0, y: 0 }}
              transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }} // smooth ease-out
            >
              <svg viewBox="0 0 100 100" className="w-[90%] h-[90%] drop-shadow-2xl overflow-visible" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="waterGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#85C1E9" />
                    <stop offset="100%" stopColor="#3498DB" />
                  </linearGradient>
                  <linearGradient id="landGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#58D68D" />
                    <stop offset="100%" stopColor="#2ECC71" />
                  </linearGradient>
                </defs>
                {/* Globe Base - Water */}
                <circle cx="50" cy="50" r="45" fill="url(#waterGrad)" />
                {/* Land masses */}
                <path d="M 25 35 Q 35 15 55 25 T 85 40 Q 90 60 70 80 T 30 75 Q 10 55 25 35 Z" fill="url(#landGrad)" />
                <path d="M 75 15 Q 85 20 80 30 Q 70 35 75 15 Z" fill="url(#landGrad)" />
                <path d="M 15 65 Q 25 75 40 60 Z" fill="url(#landGrad)" />
                {/* Lat/Long Grid lines for globe effect */}
                <path d="M 50 5 Q 75 50 50 95 Q 25 50 50 5" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
                <path d="M 15 25 Q 50 40 85 25" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
                <path d="M 5 50 Q 50 70 95 50" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
                <path d="M 15 75 Q 50 60 85 75" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
              </svg>
            </motion.div>

            {/* Graduation Cap Shape */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center top-[-30%] z-20"
              initial={{ filter: "blur(60px)", scale: 0.3, opacity: 0, rotate: 90, x: 80, y: -80 }}
              animate={{ filter: "blur(0px)", scale: 1, opacity: 1, rotate: 0, x: 0, y: 0 }}
              transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
            >
              <svg viewBox="0 0 100 100" className="w-[85%] h-[85%] drop-shadow-2xl overflow-visible" xmlns="http://www.w3.org/2000/svg">
                {/* Top Board */}
                <path d="M 50 25 L 95 40 L 50 55 L 5 40 Z" fill="#2C3E50" stroke="#000000" strokeWidth="2" strokeLinejoin="round" />
                {/* Skullcap / Base */}
                <path d="M 25 46.5 V 60 Q 50 75 75 60 V 46.5" fill="none" stroke="#000000" strokeWidth="10" strokeLinecap="round" />
                <path d="M 25 46.5 V 60 Q 50 75 75 60 V 46.5" fill="#000000" />
                {/* Tassel Button */}
                <circle cx="50" cy="40" r="3" fill="#ECF0F1" stroke="#000000" strokeWidth="1" />
                {/* Tassel String */}
                <path d="M 50 40 Q 65 45 78 58" fill="none" stroke="#2C3E50" strokeWidth="2" />
                {/* Tassel Fringes */}
                <path d="M 76 56 L 82 72 L 72 70 Z" fill="#F1C40F" stroke="#E67E22" strokeWidth="1" />
                <path d="M 78 58 L 86 70 L 76 68 Z" fill="#F39C12" />
              </svg>
            </motion.div>

          </div>

          {/* PASSBAC Animated Text with 3D Depth */}
          <div className="relative z-30 mt-2 md:mt-4 h-24 overflow-visible flex items-center justify-center perspective-[1000px]">
            <motion.h1
              className="font-heading font-black text-6xl md:text-8xl tracking-wider uppercase m-0 leading-none"
              style={{ WebkitTextStroke: "2px #000000", color: "transparent" }}
              initial={{
                opacity: 0,
                rotateX: 60,
                y: 60,
                scale: 0.8,
                textShadow: "0px 0px 0px rgba(0,0,0,0), 0px 0px 0px rgba(52,152,219,0), 0px 0px 0px rgba(46,204,113,0)"
              }}
              animate={{
                opacity: 1,
                rotateX: 0,
                y: 0,
                scale: 1,
                color: "#FFFFFF",
                textShadow: "3px 3px 0px #000000, 6px 6px 0px #3498DB, 9px 9px 0px #2ECC71"
              }}
              transition={{
                opacity: { duration: 1, delay: 1.8 },
                rotateX: { duration: 1, delay: 1.8, ease: "easeOut" },
                y: { duration: 1, delay: 1.8, ease: "easeOut" },
                scale: { duration: 1, delay: 1.8, ease: "easeOut" },
                // Color transition from transparent to white mimics the text 'filling in' or lighting up
                color: { duration: 0.8, delay: 2.8, ease: "easeInOut" },
                textShadow: { duration: 1, delay: 2.8, ease: "easeOut" }
              }}
            >
              PASSBAC
            </motion.h1>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
